'use client';

import { useEffect, useState } from 'react';

import { athenaPost } from './athena-fetch';

import { isRecord, stringField } from '@/lib/athena-utils';
import { opendoorUrl } from '@/lib/urls';

export type SessionHuman = {
  email: string;
  firstName: string | undefined;
  fullName: string | undefined;
  customerUuid: string | undefined;
};

export type AuthState =
  | { kind: 'unknown' }
  | { kind: 'authenticated'; human: SessionHuman }
  | { kind: 'unauthenticated' };

const USER_SESSION_QUERY = /* GraphQL */ `
  query UserSession {
    web {
      userSession {
        human {
          token
          email
          first_name
          full_name
          customer_uuid
        }
      }
    }
  }
`;

// Module-level cache so multiple header components share one network round-trip
// across hydration and re-renders.
let cachedAuth: AuthState | undefined;
let inflight: Promise<AuthState> | undefined;
const listeners = new Set<(state: AuthState) => void>();

function setAuth(state: AuthState) {
  cachedAuth = state;
  for (const listener of listeners) listener(state);
}

// Beacon is served on both apex (opendoor.com) and www (www.opendoor.com) per
// the Cloudflare worker. Athena's production CORS only allows *.opendoor.com
// origins (js/packages/athena/src/core/http.ts ORIGIN_REGEXP), so a
// credentialed UserSession fetch from apex is preflight-rejected and we'd
// silently fall back to the Sign in state for logged-in apex visitors.
// Canonicalize to www on hydration when the browser is on apex; SEO canonical
// + JSON-LD already point there, so this just makes the auth lookup match.
//
// Intentionally not latching a module-level "already attempted" flag: if
// location.replace gets intercepted (HSTS rewrite that no-ops, a service
// worker, etc.) we want subsequent ensureFetch calls to re-evaluate the
// hostname rather than be stranded at { kind: 'unknown' } forever. The
// same-href short-circuit keeps the call idempotent.
function redirectApexToWww(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.hostname !== 'opendoor.com') return false;
  const target = `https://www.opendoor.com${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (target === window.location.href) return false;
  window.location.replace(target);
  return true;
}

async function loadUserSession(signal?: AbortSignal): Promise<AuthState> {
  const data = await athenaPost<unknown>({
    operationName: 'UserSession',
    query: USER_SESSION_QUERY,
    variables: {},
    signal,
  });
  if (data === undefined) return { kind: 'unauthenticated' };
  return extractAuthState(data);
}

function ensureFetch(): Promise<AuthState> {
  if (cachedAuth && cachedAuth.kind !== 'unknown') return Promise.resolve(cachedAuth);
  // The apex page is about to navigate away; do not start a fetch we know would
  // be CORS-rejected and do not pollute the cache with an unauthenticated state.
  if (redirectApexToWww()) return Promise.resolve({ kind: 'unknown' });
  if (inflight) return inflight;
  inflight = loadUserSession()
    .catch(() => ({ kind: 'unauthenticated' }) as AuthState)
    .then((state) => {
      inflight = undefined;
      setAuth(state);
      return state;
    });
  return inflight;
}

export function useUserSession(): AuthState {
  const [state, setState] = useState<AuthState>(cachedAuth ?? { kind: 'unknown' });

  useEffect(() => {
    listeners.add(setState);
    if (cachedAuth) setState(cachedAuth);
    ensureFetch();
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

const SIGN_OUT_URL = opendoorUrl('/session/sign-out');
const LOGOUT_FALLBACK_URL = opendoorUrl('/logout');

export async function signOut(): Promise<void> {
  // Always clear the cached authenticated state before navigating so a bfcache
  // back/forward restore that reuses this document can't surface a stale
  // authenticated header. signOut always ends with a hard navigation, so the
  // module state is otherwise about to be discarded.
  cachedAuth = { kind: 'unauthenticated' };
  try {
    const response = await fetch(SIGN_OUT_URL, { method: 'GET', credentials: 'include' });
    // fetch resolves for 4xx/5xx, but a non-ok response means the auth service
    // didn't actually clear the session cookie; reloading would just rehydrate
    // the authenticated state. Fall through to the /logout hard navigation.
    if (!response.ok) throw new Error(`sign-out failed: ${response.status}`);
    window.location.reload();
  } catch {
    window.location.assign(LOGOUT_FALLBACK_URL);
  }
}

function nonBlank(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

// Code-point-aware first character: Array.from iterates code points so
// astral-plane characters (CJK ext B, emoji) survive intact instead of
// degrading to a lone surrogate half via [0].
function firstCodePoint(value: string): string {
  return Array.from(value)[0] ?? '';
}

export function initialsFor(human: SessionHuman): string {
  // Treat blank-after-trim as absent so a whitespace-only fullName doesn't
  // short-circuit a real firstName/email.
  const source = nonBlank(human.fullName) ?? nonBlank(human.firstName) ?? nonBlank(human.email);
  if (!source) return '';
  const parts = source.split(/\s+/);
  const first = firstCodePoint(parts[0] ?? '');
  const last = parts.length > 1 ? firstCodePoint(parts[parts.length - 1] ?? '') : '';
  return (first + last).toUpperCase();
}

// Mirrors @opendoor/auth-fe parseAuthentication: token + email are required for
// the session to count as authenticated.
function extractAuthState(data: unknown): AuthState {
  if (!isRecord(data)) return { kind: 'unauthenticated' };
  const web = data['web'];
  if (!isRecord(web)) return { kind: 'unauthenticated' };
  const userSession = web['userSession'];
  if (!isRecord(userSession)) return { kind: 'unauthenticated' };
  const human = userSession['human'];
  if (!isRecord(human)) return { kind: 'unauthenticated' };

  // Match @opendoor/auth-fe's parseAuthentication rule exactly: both a human
  // token and an email must be present for the session to count. The token
  // itself isn't surfaced on SessionHuman because nothing in beacon reads it.
  const token = stringField(human, 'token');
  const email = stringField(human, 'email');
  if (!token || !email) return { kind: 'unauthenticated' };

  return {
    kind: 'authenticated',
    human: {
      email,
      firstName: stringField(human, 'first_name'),
      fullName: stringField(human, 'full_name'),
      customerUuid: stringField(human, 'customer_uuid'),
    },
  };
}
